import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setCropSelections } from '../../../../../reducers/productConfigSlice';
import { getImageRecords } from '../../imageRecords';

const PLACEHOLDER_IMAGE = '/assets/crop-placeholder.png';

const SelectorWrapper = styled.div`
  margin: 20px 0;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
`;

const SelectorHeader = styled.div`
  margin-bottom: 16px;
`;

const SelectorTitle = styled.h5`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const SelectorDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
`;

const PoseSlotLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  margin-top: ${(props) => (props.index > 0 ? '16px' : '0')};
`;

const CropOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
`;

const CropOptionCard = styled.div`
  border: 2px solid ${(props) => (props.selected ? '#479de3' : '#e0e0e0')};
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  background-color: ${(props) => (props.selected ? '#f5f9ff' : '#fff')};
  position: relative;

  &:hover {
    border-color: ${(props) => (props.selected ? '#479de3' : '#bbb')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ExampleImageContainer = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;
  border-radius: 4px;
  background-color: #f5f5f5;
`;

const ExampleImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const OptionName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #333;
  margin-bottom: 2px;
`;

const OptionDescription = styled.div`
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  background-color: #479de3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

// Dual-mode: the regular flow renders this with no props and reads everything
// from Redux (deriving one crop block per individual record). The multi-config
// flow drives it per child via props — passing that child's crop options,
// selections, an `onCropSelectionsChange` handler, and a `posesCount` (the
// number of contiguous individual slots to render for that child).
const CropOptionsPrepaySelector = ({
  cropOptions: cropOptionsProp,
  posesCount: posesCountProp,
  cropSelections: cropSelectionsProp,
  onCropSelectionsChange,
} = {}) => {
  const dispatch = useDispatch();
  const reduxCropOptions = useSelector(
    (state) => state.shoot.crop_options || [],
  );
  const reduxCropSelections = useSelector(
    (state) => state.productConfig.cropSelections,
  );
  const product = useSelector((state) => state.productConfig.currentProduct);

  const cropOptions = cropOptionsProp ?? reduxCropOptions;
  const cropSelections = cropSelectionsProp ?? reduxCropSelections;

  // Crop styles apply per individual pose. When a posesCount is supplied
  // (multi-config child) render that many contiguous slots; otherwise render one
  // block per individual record, keyed by its slot index so cropSelections stays
  // aligned with poseSelections.
  const individualSlots =
    posesCountProp != null
      ? Array.from({ length: Math.max(posesCountProp, 1) }, (_, slotIndex) => ({
          record: { label: null },
          slotIndex,
        }))
      : getImageRecords(product)
          .map((record, slotIndex) => ({ record, slotIndex }))
          .filter(({ record }) => record.type === 'individual');

  const handleSelect = (slotIndex, option) => {
    const updated = [...cropSelections];
    updated[slotIndex] = {
      id: option.id,
      identifier: option.identifier,
      displayName: option.display_name || option.displayName,
      description: option.description,
      labCode: option.lab_code || option.labCode,
      exampleImageUrl: option.example_image_url || option.exampleImageUrl,
    };
    if (onCropSelectionsChange) {
      onCropSelectionsChange(updated);
    } else {
      dispatch(setCropSelections(updated));
    }
  };

  const multiple = individualSlots.length > 1;

  return (
    <SelectorWrapper>
      <SelectorHeader>
        <SelectorTitle>
          Select Crop Style{multiple ? ' for Each Pose' : ''}
        </SelectorTitle>
        <SelectorDescription>
          Choose how you'd like your photo{multiple ? 's' : ''} to be cropped.
        </SelectorDescription>
      </SelectorHeader>

      {individualSlots.map(({ record, slotIndex }, position) => (
        <div key={`pose-slot-${slotIndex}`}>
          {multiple && (
            <PoseSlotLabel index={position}>
              {record.label || `Pose ${position + 1}`}
            </PoseSlotLabel>
          )}
          <CropOptionsGrid>
            {cropOptions.map((option) => {
              const isSelected = cropSelections[slotIndex]?.id === option.id;
              return (
                <CropOptionCard
                  key={`${slotIndex}-${option.id}`}
                  selected={isSelected}
                  onClick={() => handleSelect(slotIndex, option)}
                >
                  <ExampleImageContainer>
                    <ExampleImage
                      src={
                        option.example_image_url ||
                        option.exampleImageUrl ||
                        PLACEHOLDER_IMAGE
                      }
                      alt={`${
                        option.display_name || option.displayName
                      } example`}
                      onError={(e) => {
                        if (e.target.src !== PLACEHOLDER_IMAGE) {
                          e.target.src = PLACEHOLDER_IMAGE;
                        }
                      }}
                    />
                  </ExampleImageContainer>
                  <OptionName>
                    {option.display_name || option.displayName}
                  </OptionName>
                  {option.description && (
                    <OptionDescription>{option.description}</OptionDescription>
                  )}
                  {isSelected && <SelectedIndicator>✓</SelectedIndicator>}
                </CropOptionCard>
              );
            })}
          </CropOptionsGrid>
        </div>
      ))}
    </SelectorWrapper>
  );
};

export default CropOptionsPrepaySelector;
